import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const StudentCard = ({ student, onInvite, onViewProfile, currentUserId }) => {
  const [isInviting, setIsInviting] = useState(false);

  const handleInvite = async () => {
    setIsInviting(true);
    try {
      await onInvite(student?.id);
    } finally {
      setIsInviting(false);
    }
  };

  const getRoleColor = (role) => {
    const colors = {
      'Developer': 'bg-blue-100 text-blue-800',
      'Project Manager': 'bg-green-100 text-green-800',
      'Business Analyst': 'bg-purple-100 text-purple-800',
      'Product Designer': 'bg-orange-100 text-orange-800'
    };
    return colors?.[role] || 'bg-gray-100 text-gray-800';
  };

  const getExperienceColor = (level) => {
    const colors = {
      'Beginner': 'text-green-600',
      'Intermediate': 'text-yellow-600',
      'Advanced': 'text-red-600'
    };
    return colors?.[level] || 'text-gray-600';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 hover:shadow-elevated transition-layout">
      <div className="flex items-start space-x-3">
        <div className="relative">
          <Image
            src={student?.avatar}
            alt={`${student?.name}'s profile`}
            className="w-12 h-12 rounded-full object-cover"
          />
          <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
            student?.isOnline ? 'bg-green-500' : 'bg-gray-400'
          }`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-foreground truncate">{student?.name}</h3>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(student?.role)}`}>
              {student?.role}
            </span>
          </div>

          <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-3">
            <div className="flex items-center space-x-1">
              <Icon name="GraduationCap" size={14} />
              <span>{student?.university}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Star" size={14} />
              <span className={getExperienceColor(student?.experienceLevel)}>
                {student?.experienceLevel}
              </span>
            </div>
          </div>

          <div className="mb-3">
            <p className="text-sm text-muted-foreground mb-2">Skills:</p>
            <div className="flex flex-wrap gap-1">
              {student?.skills?.slice(0, 3)?.map((skill, index) => (
                <span
                  key={index}
                  className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded"
                >
                  {skill}
                </span>
              ))}
              {student?.skills?.length > 3 && (
                <span className="px-2 py-1 text-xs bg-muted text-muted-foreground rounded">
                  +{student?.skills?.length - 3} more
                </span>
              )}
            </div>
          </div>

          <div className="mb-3">
            <p className="text-sm text-muted-foreground mb-1">Interests:</p>
            <p className="text-sm text-foreground line-clamp-2">{student?.interests}</p>
          </div>

          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-1 text-muted-foreground">
              <Icon name="Clock" size={14} />
              <span>Available {student?.availability}</span>
            </div>
            <div className="flex items-center space-x-1 text-muted-foreground">
              <Icon name="MapPin" size={14} />
              <span>{student?.location}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-2 mt-4 pt-3 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onViewProfile(student?.id)}
          iconName="User"
          iconPosition="left"
          iconSize={14}
          className="flex-1"
        >
          View Profile
        </Button>
        
        {student?.id !== currentUserId && (
          <Button
            variant="default"
            size="sm"
            onClick={handleInvite}
            loading={isInviting}
            iconName="UserPlus"
            iconPosition="left"
            iconSize={14}
            className="flex-1"
            disabled={student?.hasInvitation}
          >
            {student?.hasInvitation ? 'Invited' : 'Invite'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default StudentCard;